import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserPlus, Target, CheckSquare, Building2, TrendingUp, Euro, Trophy, XCircle,
  Users, Handshake, Phone, ArrowUpRight,
} from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { fmtEUR, fmtNum } from "../lib/format";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

function KpiCard({ icon: Icon, label, value, sub, onClick, accent = "#0D9488", testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all group">
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}15` }}><Icon className="w-[18px] h-[18px]" style={{ color: accent }} /></div>
        <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-teal-500 transition-colors" />
      </div>
      <p className="text-2xl font-bold font-heading text-slate-900 mt-3">{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-7">
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">{title}</p>
      <div className="grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-4 gap-3">{children}</div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    const load = () => api.get("/dashboard").then(({ data }) => setD(data)).catch(() => {});
    load();
    const h = () => load();
    window.addEventListener("crm-refresh", h);
    return () => window.removeEventListener("crm-refresh", h);
  }, []);

  if (!d) return <div className="text-slate-400 py-10">Caricamento dashboard…</div>;

  const stageData = (d.pipeline.per_fase || []).filter((s) => s.count > 0).map((s) => ({ name: s.stage, value: s.value, count: s.count }));

  return (
    <div>
      <PageHeader title="Dashboard Executive" subtitle="Panoramica in tempo reale del Business Development" />

      <Section title="Lead">
        <KpiCard testid="kpi-lead-nuovi" icon={UserPlus} label="Nuovi lead" value={d.leads.nuovi} onClick={() => nav("/lead")} accent="#1D4ED8" />
        <KpiCard testid="kpi-lead-qualificati" icon={UserPlus} label="Lead qualificati" value={d.leads.qualificati} onClick={() => nav("/lead")} accent="#0D9488" />
        <KpiCard testid="kpi-lead-contattare" icon={Phone} label="Da contattare" value={d.leads.da_contattare} onClick={() => nav("/lead")} accent="#B45309" />
        <KpiCard testid="kpi-lead-senza-attivita" icon={CheckSquare} label="Senza attività programmata" value={d.leads.senza_attivita} onClick={() => nav("/lead")} accent="#dc2626" />
      </Section>

      <Section title="Pipeline">
        <KpiCard testid="kpi-opp-aperte" icon={Target} label="Opportunità aperte" value={d.pipeline.aperte} onClick={() => nav("/opportunita")} accent="#1D4ED8" />
        <KpiCard testid="kpi-valore-totale" icon={Euro} label="Valore totale pipeline" value={fmtEUR(d.pipeline.valore_totale)} onClick={() => nav("/pipeline")} accent="#0D9488" />
        <KpiCard testid="kpi-valore-ponderato" icon={TrendingUp} label="Valore ponderato" value={fmtEUR(d.pipeline.valore_ponderato)} onClick={() => nav("/pipeline")} accent="#6D28D9" />
        <KpiCard testid="kpi-vinte" icon={Trophy} label="Opportunità vinte" value={d.pipeline.vinte} sub={`${d.pipeline.perse} perse`} onClick={() => nav("/opportunita")} accent="#059669" />
      </Section>

      <div className="grid lg:grid-cols-2 gap-3 mb-7 min-w-0">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm font-heading font-semibold text-slate-700 mb-4">Opportunità per fase</p>
          {stageData.length === 0 ? <p className="text-slate-400 text-sm py-10 text-center">Nessun dato</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stageData} margin={{ left: -10 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip formatter={(v) => fmtEUR(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>{stageData.map((_, i) => <Cell key={i} fill={`hsl(174 70% ${45 - i * 3}%)`} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm font-heading font-semibold text-slate-700 mb-4">Funnel Business Development</p>
          <div className="space-y-2.5">
            {[
              { l: "Aziende prospect", v: d.business_dev.prospect, c: "#94a3b8" },
              { l: "Decision maker identificati", v: d.business_dev.decision_maker, c: "#64748b" },
              { l: "Contatti avviati", v: d.business_dev.contatti_avviati, c: "#0D9488" },
              { l: "Meeting ottenuti", v: d.business_dev.meeting_ottenuti, c: "#6D28D9" },
              { l: "Opportunità generate", v: d.business_dev.opportunita_generate, c: "#1D4ED8" },
              { l: "Clienti acquisiti", v: d.business_dev.clienti, c: "#059669" },
            ].map((row, i, arr) => {
              const max = Math.max(...arr.map((r) => r.v), 1);
              return (
                <div key={row.l}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-slate-600">{row.l}</span><span className="font-mono font-semibold text-slate-800">{fmtNum(row.v)}</span></div>
                  <div className="h-6 rounded-md bg-slate-100 overflow-hidden"><div className="h-full rounded-md flex items-center transition-all" style={{ width: `${Math.max((row.v / max) * 100, 4)}%`, background: row.c }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Section title="Attività">
        <KpiCard testid="kpi-att-oggi" icon={CheckSquare} label="Attività di oggi" value={d.attivita.oggi} onClick={() => nav("/attivita?view=oggi")} accent="#0D9488" />
        <KpiCard testid="kpi-att-scadute" icon={XCircle} label="Attività scadute" value={d.attivita.scadute} onClick={() => nav("/attivita?view=scadute")} accent="#dc2626" />
        <KpiCard testid="kpi-att-followup" icon={Handshake} label="Follow-up da effettuare" value={d.attivita.followup} onClick={() => nav("/attivita")} accent="#B45309" />
        <KpiCard testid="kpi-att-meeting" icon={Users} label="Appuntamenti / Meeting" value={d.attivita.meeting} onClick={() => nav("/attivita")} accent="#6D28D9" />
      </Section>

      <Section title="Business Development">
        <KpiCard testid="kpi-bd-prospect" icon={Building2} label="Aziende prospect" value={d.business_dev.prospect} onClick={() => nav("/aziende")} accent="#64748b" />
        <KpiCard testid="kpi-bd-dm" icon={Users} label="Decision maker" value={d.business_dev.decision_maker} onClick={() => nav("/contatti")} accent="#0D9488" />
        <KpiCard testid="kpi-bd-opp" icon={Target} label="Opportunità generate" value={d.business_dev.opportunita_generate} onClick={() => nav("/opportunita")} accent="#1D4ED8" />
        <KpiCard testid="kpi-bd-clienti" icon={Trophy} label="Clienti acquisiti" value={d.business_dev.clienti} onClick={() => nav("/clienti")} accent="#059669" />
      </Section>
    </div>
  );
}
